import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { HighlightedQuote } from '../components/ui/HighlightedQuote';
import { TestimonialCard } from '../components/ui/TestimonialCard';
import { ProductShowcase } from '../components/ui/ProductShowcase';
import { StickyCTABar } from '../components/ui/StickyCTABar';
import { CTAButton } from '../components/ui/CTAButton';
const SectionDivider = () => {
  return <div className="flex items-center justify-center my-10 md:my-12" data-id="element-40">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" data-id="element-41"></div>
      <div className="mx-4 w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-42"></div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" data-id="element-43"></div>
    </div>;
};
export function AdvertorialPage() {
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px'
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] text-gray-900 font-serif selection:bg-accent-light selection:text-accent-hover" data-id="element-44">
      {/* Navigation / Header - Minimal Editorial Style */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm py-4 sticky top-0 z-40 transition-all duration-300" data-id="element-45">
        <div className="max-w-4xl mx-auto px-4 md:px-6 flex justify-between items-center" data-id="element-46">
          <div className="font-serif font-black text-xl md:text-2xl tracking-tight" data-id="element-47">
            THE VEGAN JOURNAL
          </div>
        </div>
      </header>

      <main className="max-w-[720px] mx-auto px-5 md:px-8 py-10 md:py-16 lg:py-24" data-id="element-48">
        {/* Hero Section */}
        <article className="prose prose-lg prose-stone max-w-none prose-p:font-serif prose-headings:font-serif" data-id="element-49">
          <motion.div {...fadeIn} className="mb-10 md:mb-16 text-center" data-id="element-50">
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-bold tracking-widest text-accent uppercase bg-accent/10 rounded-full font-sans" data-id="element-51">
              Personal Story
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-[1.1] mb-6 md:mb-8 text-gray-900 tracking-tight" data-id="element-52">
              The Night I Almost Ordered Steak
            </h1>
            <h2 className="text-xl md:text-2xl text-gray-600 font-serif leading-relaxed max-w-xl mx-auto font-light" data-id="element-53">
              I sat in the restaurant for 47 minutes. Staring at the menu.
              Hating myself.
            </h2>
          </motion.div>

          {/* Author Byline */}
          <div className="flex items-center justify-center gap-4 mb-10 md:mb-14 font-sans text-sm text-gray-500 border-y border-gray-200 py-4" data-id="element-54">
            <div className="flex items-center gap-2" data-id="element-55">
              <div className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-56">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100" alt="Sarah Jenkins" className="w-full h-full object-cover" loading="eager" decoding="async" width="100" height="100" data-id="element-57" />
              </div>
              <span data-id="element-58">
                By{' '}
                <span className="font-bold text-gray-900 underline decoration-gray-300 underline-offset-4" data-id="element-59">
                  Sarah Jenkins
                </span>
              </span>
            </div>
            <span className="text-gray-300" data-id="element-60">•</span>
            <span data-id="element-61">7 min read</span>
          </div>

          {/* Hero Image */}
          <motion.figure {...fadeIn} className="my-10 md:my-14 -mx-5 md:-mx-10" data-id="element-62">
            <div className="relative aspect-[16/9] overflow-hidden" data-id="element-63">
              <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&q=80&w=1200" alt="Restaurant menu on table" className="w-full h-full object-cover grayscale-[20%] hover:scale-105 transition-transform duration-700 ease-out" loading="eager" decoding="async" width="1200" height="675" data-id="element-64" />
            </div>
          </motion.figure>

          {/* Main Content */}
          <div className="space-y-6 md:space-y-8 text-lg md:text-[1.15rem] leading-[1.8] text-gray-800 font-serif" data-id="element-65">
            <p className="first-letter:text-5xl md:first-letter:text-6xl first-letter:font-bold first-letter:mr-3 first-letter:float-left first-letter:text-accent first-letter:leading-[0.8]" data-id="element-66">
              I need to tell you about the worst night of my life as a vegan.
            </p>
            <p data-id="element-67">
              Not the night I got food poisoning from a dodgy falafel wrap. Not
              the Christmas dinner where my uncle spent two hours explaining why
              humans are "supposed" to eat meat.
            </p>
            <p data-id="element-68">
              The night I sat alone in a restaurant, seven years into veganism,
              and{' '}
              <span className="font-semibold text-gray-900 bg-yellow-50 px-1" data-id="element-69">
                genuinely considered ordering a steak.
              </span>
            </p>
            <p className="font-bold text-gray-900 text-xl" data-id="element-70">
              Not because I wanted one.
            </p>
            <p data-id="element-71">
              Because I was so desperate to feel like myself again that I was
              willing to betray everything I believed in.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14 -mx-5 md:-mx-10" data-id="element-72">
              <SectionDivider data-id="element-73" />
            </motion.figure>

            <HighlightedQuote data-id="element-74">
              I sat there for 47 minutes, staring at the menu.
            </HighlightedQuote>

            <p data-id="element-75">
              <span className="font-bold text-gray-900" data-id="element-76">
                I haven't told anyone this story.
              </span>{' '}
              Not my vegan friends. Not my partner. Definitely not my non-vegan
              family.
            </p>
            <p data-id="element-77">
              Seven years of being "that vegan." The one who brought her own food to barbecues. Who converted three friends. Who cried during Dominion. And here I was, about to secretly order a steak.{' '}
            </p>
            <p className="font-semibold text-gray-900" data-id="element-78">
              You don't explain it. You sit there and hate yourself.
            </p>

            <SectionDivider data-id="element-79" />

            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 font-sans tracking-tight" data-id="element-80">
              The Brain Fog That Woudn't Go.
            </h2>

            <p data-id="element-81">Six months before that night, my brain started failing me.</p>
            <p data-id="element-82">
              I'd be mid-conversation and my mind would just go totally blank.
              I'd stand there, mouth half-open, waiting for my own brain to
              catch up.
            </p>

            <p data-id="element-83">
              Then came the fatigue. Not tiredness. I know tiredness. This was
              different.
            </p>

            <p data-id="element-84">
              This was cancelling plans because the thought of going out seemed
              exhausting.
            </p>

            <p data-id="element-85">
              This was sitting at my desk at 2pm unable to understand the same
              email I'd read six times already.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14 -mx-5 md:-mx-10" data-id="element-86">
              <div className="relative aspect-[16/9] overflow-hidden rounded-lg" data-id="element-87">
                <img src="https://cdn.magicpatterns.com/uploads/v8WY2J5iogAQnBmXHFnoEq/ChatGPT_Image_Jan_23,_2026,_12_28_29_AM.png" alt="Laptop on desk showing blurred email" className="w-full h-full object-cover" loading="lazy" decoding="async" width="1200" height="675" data-id="element-88" />
              </div>
            </motion.figure>

            <p data-id="element-89">
              I was doing everything right as a Vegan. B12, iron, Multivitamins.
              I tracked my meals. I optimized my protein. I did everything the
              forums and dietitians told me to do.
            </p>

            <p data-id="element-90">Everything pointed to one thing...</p>

            <p className="font-bold text-xl text-gray-900 bg-yellow-50 px-2 py-1 inline-block" style={{
            backgroundColor: '#fefbf8'
          }} data-id="element-91">
              My body just couldn't handle being vegan.
            </p>

            <SectionDivider data-id="element-92" />

            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 font-sans tracking-tight" data-id="element-93">
              The Shocking Discovery
            </h2>

            <p data-id="element-94">
              In the quiet corners of the internet I started to notice something
              I couldn't ignore.
            </p>
            <p data-id="element-95">Vegans who ate meat after years kept saying the same thing:</p>

            <div className="bg-gray-50 p-6 md:p-8 rounded-lg border border-gray-100 my-8 italic text-gray-700 space-y-4 font-serif text-lg" data-id="element-96">
              <p className="flex gap-3" data-id="element-97">
                <span className="text-accent font-bold" data-id="element-98">"</span>I felt better
                almost immediately.
              </p>
              <p className="flex gap-3" data-id="element-99">
                <span className="text-accent font-bold" data-id="element-100">"</span>
                Within days, my brain fog lifted.
              </p>
              <p className="flex gap-3" data-id="element-101">
                <span className="text-accent font-bold" data-id="element-102">"</span>I hate admitting
                it, but I felt like myself again.
              </p>
            </div>

            <p data-id="element-103">
              I read those posts with a mixture of horror and hunger. That's
              when the thought started creeping in:{' '}
              <span className="font-bold text-gray-900 bg-yellow-50 px-1" data-id="element-104">
                What if they're right?
              </span>
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14" data-id="element-105">
              <div className="max-w-2xl mx-auto" data-id="element-106">
                <div className="relative overflow-hidden rounded-lg border-2 border-gray-200 shadow-md bg-white" data-id="element-107">
                  <img src="https://cdn.magicpatterns.com/uploads/s4r9bDQYdA2ZShqGsvjVcs/www.reddit.com_r_vegan_comments_1o8mzui_for_those_who_say_they_feel_better_after__(1).png" alt="Reddit post from r/vegan about feeling better after eating meat" className="w-full h-auto" loading="lazy" decoding="async" width="800" height="600" data-id="element-108" />
                </div>
                <figcaption className="text-xs md:text-sm text-gray-500 mt-4 italic text-left font-sans" data-id="element-109">
                  Reddit post from r/vegan
                </figcaption>
              </div>
            </motion.figure>

            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 font-sans tracking-tight" data-id="element-110">
              The Turning Point
            </h2>

            <p className="font-semibold text-gray-900" data-id="element-111">
              Which brings me back to that restaurant. 47 minutes.
            </p>
            <p data-id="element-112">
              That's how long I sat there with the menu open, pretending to
              decide what I wanted, while a war raged inside me.
            </p>
            <p data-id="element-113">
              On one side: seven years of conviction. The animals I'd saved. The
              person I'd worked so hard to become.
            </p>
            <p data-id="element-114">
              On the other side: a brain that wouldn't work. A body that felt
              like it was betraying me. A growing suspicion that the only way
              out was through something I'd sworn never to do.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14 -mx-5 md:-mx-10" data-id="element-115">
              <div className="relative aspect-[16/9] overflow-hidden rounded-lg" data-id="element-116">
                <img src="https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&q=80&w=1200" alt="Grilled steak on a plate" className="w-full h-full object-cover grayscale-[20%] hover:scale-105 transition-transform duration-700 ease-out" loading="lazy" decoding="async" width="1200" height="675" data-id="element-117" />
              </div>
            </motion.figure>

            <p className="font-medium text-gray-900 text-xl border-l-4 border-accent pl-4 py-2 my-6" data-id="element-118">
              I thought, if I do this will I finally feel "normal again"
            </p>
            <p data-id="element-119">
              <strong data-id="element-120">I decided not to order the steak.</strong> Not because I found some hidden reserve of willpower. But because my phone buzzed.
            </p>
            <p data-id="element-121">
              A received a notification from a vegan health forum I'd posted in
              weeks earlier. Someone had replied to my desperate 2am thread
              about brain fog.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14" data-id="element-122">
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-gray-100 max-w-sm mx-auto transform rotate-1" data-id="element-123">
                <div className="flex items-center gap-3 mb-3" data-id="element-124">
                  <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs" data-id="element-125">
                    R
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-126">
                    <span className="font-bold text-gray-900" data-id="element-127">
                      vegan_runner
                    </span>{' '}
                    • 2h ago
                  </div>
                </div>
                <p className="text-sm md:text-base font-sans leading-relaxed" data-id="element-128">
                  "Have you tried Creatine? It's not just for bodybuilders. Our
                  brains need it too, and we get zero from plants. Changed my
                  life."
                </p>
              </div>
              <figcaption className="text-xs md:text-sm text-gray-500 mt-4 italic text-center font-sans" data-id="element-129">
                The notification that changed everything.
              </figcaption>
            </motion.figure>

            <p data-id="element-130">I put the menu down. I ordered the vegetable curry.</p>

            <p data-id="element-131">
              When I got home, I couldn't sleep. I stayed up until nearly 4am
              reading studies, forums, and obscure threads I'd never seen before
              about creatine.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14" data-id="element-132">
              <div className="max-w-lg mx-auto" data-id="element-133">
                <div className="relative overflow-hidden rounded-lg shadow-lg border border-gray-200" data-id="element-134">
                  <img src="https://cdn.magicpatterns.com/uploads/66Q4knEctGEYCeAdBQhoeY/Creatine_availability.png" alt="Chart showing creatine availability in vegetarians, vegans, and omnivores" className="w-full h-auto object-contain" loading="lazy" decoding="async" width="800" height="600" data-id="element-135" />
                </div>
              </div>
            </motion.figure>

            <SectionDivider data-id="element-136" />

            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 font-sans tracking-tight" data-id="element-137">
            How to Get Creatine
            <span className="block" data-id="element-138">(Without Meat)</span>
            </h2>


            <p data-id="element-139">
              I found a company called Loomy. They make gummies specifically to
              help vegans and vegetarians with creatine deficiencies.
            </p>

            <p data-id="element-140">I tried them for a month. Here's what happened.</p>

            {/* UGC Photo - Now full width on mobile */}
            <motion.figure {...fadeIn} className="my-10 md:my-14" data-id="element-141">
              <div className="aspect-square w-full md:max-w-sm md:mx-auto rounded-lg overflow-hidden shadow-lg" data-id="element-142">
                <img src="https://cdn.shopify.com/s/files/1/0936/8453/1542/files/Untitled_design_39.png?v=1769139568" alt="Hand holding Loomy Creatine Gummies package" className="w-full h-full object-cover" loading="lazy" decoding="async" width="800" height="800" data-id="element-143" />
              </div>
            </motion.figure>

            <div className="space-y-6 md:space-y-8" data-id="element-144">
              <p data-id="element-145">
                <span className="font-bold text-gray-900" data-id="element-146">First few days</span>{' '}
                of taking Loomy, I felt nothing.
              </p>

              <p data-id="element-147">
                <span className="font-bold text-gray-900" data-id="element-148">Day five or six</span>
                , I noticed something small. When I woke up I didn't immediately
                feel like I needed more sleep.
              </p>

              <p data-id="element-149">
                By <span className="font-bold text-gray-900" data-id="element-150">week two</span>,
                the brain fog was lifting. I wasn't searching for words anymore.
                My brain just...{' '}
                <span className="font-bold text-gray-900" data-id="element-151">worked.</span>
              </p>

              <p data-id="element-152">
                <span className="font-bold text-gray-900" data-id="element-153">Week three</span>, my
                energy came back. Not a random spike. Just a steady, normal
                energy I'd forgotten I used to have.
              </p>

              <p data-id="element-154">
                By the{' '}
                <span className="font-bold text-gray-900" data-id="element-155">
                  end of the first month
                </span>
                ,
              </p>

              <p className="font-bold text-2xl md:text-3xl text-gray-900 border-l-4 border-accent pl-6 py-4 my-8 bg-accent/5 rounded-r-lg" style={{
              fontSize: '20px',
              padding: '11px'
            }} data-id="element-156">
                I felt like myself again.
              </p>
            </div>

            <ProductShowcase data-id="element-157" />

            <div className="my-12 md:my-16" data-id="element-158">
              <h3 className="text-xl md:text-2xl font-bold mb-8 text-gray-900 font-sans tracking-tight text-center" data-id="element-159">
                What Others Are Saying
              </h3>
              <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-160">
                <TestimonialCard quote="I was three weeks away from adding fish back into my diet. Loomy stopped me from having to make that choice. I can't explain how much that means." author="Sarah" role="Vegan 5 years" imageSrc="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100" data-id="element-161" />
                <TestimonialCard quote="I cried when it worked. Because I'd genuinely believed I was going to have to choose between my ethics and my brain. And I didn't." author="Emma" role="Vegan 6 years" imageSrc="https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?auto=format&fit=crop&q=80&w=100" data-id="element-162" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 md:p-12 my-12 shadow-sm" data-id="element-163">
              <div className="space-y-5 text-base md:text-lg leading-relaxed" data-id="element-164">
                <p className="text-gray-800" data-id="element-165">
                  I don't know where you are right now.
                </p>
                <p className="text-gray-700" data-id="element-166">
                  Maybe you're in the restaurant. Menu open. 47 minutes in.
                  Wondering if tonight's the night you finally give up.
                </p>
                <p className="text-gray-700" data-id="element-167">
                  Maybe you're still months away from that moment, and can feel
                  yourself getting closer.
                </p>
                <p className="text-gray-700" data-id="element-168">
                  Maybe you've already slipped, and you're carrying the guilt of
                  it.
                </p>
              </div>

              <div className="mt-8 pt-8 border-t-2 border-gray-200 space-y-4" data-id="element-169">
                <p className="text-lg md:text-xl text-gray-800" data-id="element-170">
                  Wherever you are: you don't have to break.
                </p>
                <p className="text-lg md:text-xl text-gray-800" data-id="element-171">
                  There's another way. I found it. Now you know it exists.
                </p>
              </div>

              <div className="mt-10 text-center" data-id="element-172">
                <CTAButton size="lg" className="mx-auto shadow-xl hover:shadow-2xl transform hover:-translate-y-1 w-full md:w-auto text-lg px-8 py-4" data-id="element-173">
                  Get Loomy Now
                </CTAButton>
              </div>
            </div>
          </div>
        </article>
      </main>

      <footer className="bg-white py-12 md:py-16 border-t border-gray-200" data-id="element-174">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center text-gray-500 text-sm" data-id="element-175">
          <div className="mb-8" data-id="element-176">
            <span className="font-serif font-black text-xl tracking-tight text-gray-900" data-id="element-177">
              THE VEGAN JOURNAL
            </span>
          </div>
          <p className="mb-6" data-id="element-178">
            © {new Date().getFullYear()} Loomy. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-8 mb-8 text-xs md:text-sm font-medium tracking-wide uppercase" data-id="element-179">
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-180">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-181">
              Terms of Service
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-182">
              Contact Us
            </a>
          </div>
          <p className="max-w-xl mx-auto text-[10px] md:text-xs text-gray-400 px-4 leading-relaxed" data-id="element-183">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease. The information provided is for
            informational purposes only and is not intended as a substitute for
            advice from your physician or other health care professional.
          </p>
        </div>
      </footer>

      <StickyCTABar data-id="element-184" />
    </div>;
}